import React, { useCallback, useEffect, useRef } from "react"
import {
  DefaultThreadMessage,
  StyledThreadsList,
  ThreadCardContainer,
  ThreadSideTick,
  ThreadSubjectAndDate
} from "./Threads"
import { CenteredLoadingIndicator } from "../views/molecules/Molecules"
import { InAppMessagingCreateNewThreadForm, NewMessageButtonContainer } from "./NewThread"
import { MessagingErrorContainer, ThreadMessagesContainer } from "./Messages"
import { InAppMessagingThread } from "./inAppMessagingTypes"
import { MessageReplyContainer } from "./MessageReplyContainer"
import { useInAppMessaging } from "../context/messaging/InAppMessagingContext"
import useGetThreads from "./hooks/useGetThreads"
import { useHistory } from "react-router"
import { useParams } from "react-router-dom"
import { useIntl } from "react-intl"
import { Box, Stack, Theme, useMediaQuery, useTheme } from "@carrotfertility/carotene-core"
import { useCarrotCompanionCarrotPlanMessage } from "#/components/care-team/hooks/useCarrotCompanion"
import { updateCarrotPlanMessageDismissTimestamp } from "#/components/views/modal/utils/localStorageUtils"

/**
 * Stands in for "no thread" so the pane can render the canned card. Not a thread id the API can
 * return — keep the literal in one place so the sentinel and the comparisons against it cannot
 * drift apart.
 *
 * Deliberately NOT exported: nothing outside this file uses it, and knip fails the lint on an unused
 * export.
 */
const EMPTY_STATE_THREAD_ID = -1

/**
 * A scroll container filling the conversation pane, for the branches that are one piece of content
 * rather than a thread above a composer.
 *
 * The pane itself clips (`overflow: hidden`), which is what keeps a long thread from spilling past the
 * layout — so anything that can be taller than the pane has to scroll inside it.
 */
function PaneScroll({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <Box
      data-testid="pane-scroll"
      sx={{
        flex: 1,
        minBlockSize: 0,
        overflowX: "hidden",
        overflowY: "auto"
      }}
    >
      {children}
    </Box>
  )
}

function DefaultThreadCard(): JSX.Element {
  const { selectedThread, setSelectedThread, showNewThreadForm, setShowNewThreadForm, setShowOnlyThreadsList } =
    useInAppMessaging()
  const intl = useIntl()

  return (
    // NO WRAPPER. Main puts this row inside a `Box` carrying `marginBlockEnd: 138px` and
    // `borderBlockEnd: 1px solid rgba(0, 0, 0, 0.1)`, and both are gone:
    //
    //  - The margin was the gradient's counterweight — clearance so the button, which pulled itself up
    //    172px over the end of the list, could not cover the last row. The button sits in flow below the
    //    list now, so the clearance would just be a blank band inside a bounded column.
    //  - The rule DOUBLED the one this row already draws. `ThreadCardContainer` gives every row a
    //    `borderWidth.sm` bottom in `background.hover` (#F0F0F0), this row included, and the wrapper's
    //    sat directly beneath it: 2px in two greys, #F0F0F0 over ≈#E6E6E6, under this row alone and no
    //    other. It read as a visibly darker, thicker line and was reported as one.
    //
    // Removing the rule left the `Box` with nothing to carry, so the row is its own element now. The list
    // still closes off under its last row — by that row's own rule, the same one every row above it draws.
    <ThreadCardContainer
      onClick={() => {
        // canned response
        setSelectedThread({ threadId: EMPTY_STATE_THREAD_ID })
        setShowNewThreadForm(false)
        setShowOnlyThreadsList(false)
      }}
    >
      <ThreadSideTick active={selectedThread?.threadId === EMPTY_STATE_THREAD_ID && !showNewThreadForm} />
      <ThreadSubjectAndDate
        thread={{
          date: intl.formatMessage({ defaultMessage: "From Carrot" }),
          subject: intl.formatMessage({ defaultMessage: "Get started with Messages" })
        }}
      />
    </ThreadCardContainer>
  )
}

function MemberMessagesContainer(): JSX.Element {
  const { thread: threadParam } = useParams<{ thread: string }>()
  const history = useHistory()
  const hideColumn = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"))
  const intl = useIntl()
  const theme = useTheme()

  const {
    showNewThreadForm,
    setShowNewThreadForm,
    setShowOnlyThreadsList,
    showOnlyThreadsList,
    selectedThread,
    setSelectedThread
  } = useInAppMessaging()

  const {
    data: threadsData,
    isLoading: isGetThreadsLoading,
    isError: isGetThreadsError,
    isSuccess: isGetThreadsSuccess
  } = useGetThreads()

  const { data: cpMessage } = useCarrotCompanionCarrotPlanMessage()

  // The param this container has already acted on. Selection cannot simply key on both `threadParam`
  // and `selectedThread`: the threads list selects by calling `setSelectedThread` WITHOUT touching the
  // URL, so an effect that re-ran on every selection change would re-resolve the now-stale param and
  // undo the member's click. So the URL is treated as an instruction, resolved once per change.
  //
  // `null` — rather than `undefined` — is "nothing resolved yet", because `undefined` is what the
  // param itself reads as at `/messages`.
  const resolvedParam = useRef<{ param: string | undefined } | null>(null)

  useEffect(() => {
    if (!isGetThreadsSuccess || showNewThreadForm) {
      return
    }
    if (resolvedParam.current !== null && resolvedParam.current.param === threadParam) {
      return
    }
    resolvedParam.current = { param: threadParam }

    const newestThreadOrCannedCard = threadsData.threads[0] ?? { threadId: EMPTY_STATE_THREAD_ID }

    if (!threadParam) {
      // The URL names no thread, which is not the same as "no thread should be open": fill in a
      // default, never clear a selection the member already has. The `history.replace("/messages")`
      // below is itself a param change, so it re-enters here with a selection already made.
      if (selectedThread == null) {
        setSelectedThread(newestThreadOrCannedCard)
      }
      return
    }

    // The key has to name a thread id in FULL. A loose parse resolves a malformed key to whatever it
    // starts with — `parseInt("102junk", 10)` is `102` — and so would open a real thread on a URL that
    // names none; `Number` would swallow `101 `, `+101` and `1e3` the same way. Comparing the id's own
    // decimal spelling accepts only the form the URL is written in, and sends everything else to the
    // fallback below.
    const linkedThread = threadsData.threads.find((t) => String(t.threadId) === threadParam)
    if (linkedThread) {
      setSelectedThread(linkedThread)
      // A URL that NAMES a thread also opens the pane holding it. The shell seeds the pane from the
      // route at mount, which covers a deep link but not a navigation from the threads list: that
      // keeps one mount, so the `true` the list left behind would survive and hide the thread on a
      // narrow viewport. Redundant on a deep link, where this sets `false` over `false`.
      setShowOnlyThreadsList(false)
      return
    }

    // An unknown thread id is dropped from the URL — `replace`, so back does not walk into the dead
    // link again — and falls back to the newest thread, or to the canned card when the member has none.
    history.replace("/messages")
    setSelectedThread(newestThreadOrCannedCard)
  }, [
    threadParam,
    selectedThread,
    setSelectedThread,
    setShowOnlyThreadsList,
    showNewThreadForm,
    isGetThreadsSuccess,
    history,
    threadsData
  ])

  // Must stay out of the selection effect above, and must require the id to exist: the dismissal is
  // stamped only when the member has a Carrot Plan message AND has that thread open. An unguarded
  // comparison matches `undefined === undefined` before either query resolves.
  const carrotPlanThreadId = cpMessage?.thread?.threadId
  useEffect(() => {
    if (carrotPlanThreadId != null && selectedThread?.threadId === carrotPlanThreadId) {
      updateCarrotPlanMessageDismissTimestamp()
    }
  }, [carrotPlanThreadId, selectedThread?.threadId])

  const mapThreads = useCallback(
    (threads: InAppMessagingThread[]) => {
      return threads.map((thread: InAppMessagingThread) => {
        return (
          <ThreadCardContainer
            key={thread.threadId}
            onClick={() => {
              setSelectedThread(thread)
              setShowNewThreadForm(false)
              setShowOnlyThreadsList(false)
            }}
          >
            <ThreadSideTick active={selectedThread?.threadId === thread.threadId && !showNewThreadForm} />
            <Stack
              direction="row"
              spacing={theme.spacing(theme.tokens.spacing.sm)}
              width="100%"
              sx={{ overflow: "hidden", backgroundColor: (theme) => theme.palette.background.paper }}
            >
              <ThreadSubjectAndDate
                thread={{
                  date: intl.formatDate(thread.updatedAt, { month: "short", day: "numeric" }),
                  subject: thread.subject
                }}
              />
              {thread.hasUnreadMessages && (
                <Box
                  sx={{
                    display: "flex",
                    inlineSize: "9px",
                    justifyContent: "flex",
                    alignItems: "center",
                    marginBlockStart: "4px"
                  }}
                >
                  <Box
                    sx={{
                      display: "block",
                      backgroundColor: (theme) => theme.palette.primary.accent,
                      blockSize: (theme) => theme.spacing(theme.tokens.spacing.xs),
                      inlineSize: (theme) => theme.spacing(theme.tokens.spacing.xs),
                      borderRadius: "50%"
                    }}
                  />
                </Box>
              )}
            </Stack>
          </ThreadCardContainer>
        )
      })
    },
    [
      selectedThread?.threadId,
      setSelectedThread,
      showNewThreadForm,
      setShowNewThreadForm,
      setShowOnlyThreadsList,
      intl,
      theme
    ]
  )

  const threadsListScrollRef = useRef(null)
  function scrollThreadsListToTop(): void {
    threadsListScrollRef.current.scrollIntoView()
  }

  return (
    <>
      {isGetThreadsLoading && <CenteredLoadingIndicator />}
      {isGetThreadsError && <MessagingErrorContainer />}
      {isGetThreadsSuccess && (
        // MAIN'S LAYOUT, BOUNDED. The columns, the widths, the backgrounds and the 8px radius are main's
        // — CONAPP-183's restyle of this tab (a bordered card at a 48px radius with a footer rule) was
        // reverted and is not coming back here. What is NOT main's is the sizing: this fills the
        // viewport bound `PageLayout` hands down instead of running past the bottom of the window.
        //
        // THE `calc(100vh - 216px)` / `calc(100vh - 248px)` PAIR IS GONE, and must not come back. Those
        // constants wrote down the pixel height of the chrome above the pane, so they were already wrong
        // by the tab bar's height — the composer sat below the fold, which is what a member reported.
        // `minmax(0, 1fr)` and the flex chain under it do that subtraction instead, so nothing has to be
        // kept in step with the header.
        <Box
          data-testid="messages-layout"
          sx={(theme) => ({
            display: "grid",
            // One column below `lg`, where exactly one pane is on screen at a time (`hideColumn`). The
            // `lg` template mirrors main's 4/8 `Grid` split rather than inventing a width.
            gridTemplateColumns: { xs: "1fr", lg: "33.333333% minmax(0, 1fr)" },
            // A SINGLE row that refuses to grow past its track, which is what lets the two panes scroll
            // inside their own boxes instead of stretching the row to their content.
            gridTemplateRows: "minmax(0, 1fr)",
            columnGap: theme.spacing(theme.tokens.spacing.md),
            // Claims the space the `fillViewport` chain hands down. Inert without it — a `flex` child of
            // a non-flex parent — which is why the panes simply grow in the component suites that mount
            // this alone.
            flex: 1,
            minBlockSize: 0
          })}
        >
          <Box
            sx={(theme) => ({
              display: hideColumn && !showOnlyThreadsList ? "none" : "flex",
              // A COLUMN, so the list can take the slack and the button sit under it. Main had `block`
              // here, with the button pulling itself up over the list under a gradient; that needs a page
              // that scrolls (see `NewMessageButtonContainer`).
              flexDirection: "column",
              minBlockSize: 0,
              overflowY: "hidden",
              backgroundColor: theme.palette.background.paper,
              // Main's radius, and written as the bare token: `sx` multiplies a numeric `borderRadius` by
              // `shape.borderRadius` (4), so this is 8px. `theme.borderRadius(...)` here would be 32.
              borderRadius: theme.tokens.borderRadius.md
            })}
          >
            <StyledThreadsList>
              <div ref={threadsListScrollRef} />
              <Box component="nav">
                {mapThreads(threadsData.threads)}
                <DefaultThreadCard />
              </Box>
            </StyledThreadsList>
            <NewMessageButtonContainer
              disabled={showNewThreadForm}
              onClick={() => {
                setShowOnlyThreadsList(false)
                setShowNewThreadForm(true)
              }}
            />
          </Box>
          <Box
            data-testid="conversation-detail"
            sx={{
              display: hideColumn && showOnlyThreadsList ? "none" : "flex",
              flexDirection: "column",
              minBlockSize: 0,
              overflow: "hidden"
            }}
          >
            {showNewThreadForm ? (
              // The pane CLIPS what overflows it, so every branch rendered into it needs a scroller of
              // its own or its bottom becomes unreachable. The composer is the tall one — subject field,
              // eleven-row textarea, button pair — and the empty state's prose is not short either.
              <PaneScroll>
                <InAppMessagingCreateNewThreadForm
                  newestThread={threadsData.threads[0] ?? { threadId: EMPTY_STATE_THREAD_ID }}
                  scrollThreadsListToTop={scrollThreadsListToTop}
                />
              </PaneScroll>
            ) : selectedThread?.threadId === EMPTY_STATE_THREAD_ID ? (
              <PaneScroll>
                <DefaultThreadMessage />
              </PaneScroll>
            ) : (
              <>
                {/* The conversation's own scroll container, so a long thread never moves the list. */}
                <Box
                  data-testid="thread-scroll"
                  sx={(theme) => ({
                    flex: 1,
                    minBlockSize: 0,
                    overflowX: "hidden",
                    overflowY: "auto",
                    marginBlockEnd: theme.spacing(theme.tokens.spacing.md),
                    marginInlineStart: { lg: theme.spacing(theme.tokens.spacing.md) }
                  })}
                >
                  <ThreadMessagesContainer />
                </Box>
                {/* No `justify-content: space-between` holding this down: the scroller above takes the
                    slack, so the composer sits at the bottom of the pane on its own. */}
                <MessageReplyContainer thread={selectedThread} scrollThreadsListToTop={scrollThreadsListToTop} />
              </>
            )}
          </Box>
        </Box>
      )}
    </>
  )
}

export default MemberMessagesContainer
